// validators/zip-code-validator.js
import { z } from "zod";
import { ValidatorInterface } from "../validator-interface";
import { getRestUrlPrefix } from "../utils/environment";

export class ZipCodeValidator extends ValidatorInterface {
    constructor() {
        super();
        // Simple cache for validation results
        this._cache = new Map();
        // Store ongoing requests to prevent duplicate API calls
        this._pendingRequests = new Map();
    }

    // Return base schema with format validation only
    getZodSchema(baseSchema, options = {}) {
        const message = options.message || "Please enter a 5-digit ZIP code";
        // Apply regex to string schemas. Handle potential non-string base types.
        if (baseSchema instanceof z.ZodString) {
            return baseSchema.regex(/^\d{5}$/, { message });
        } else {
            // eslint-disable-next-line no-console
            console.warn("ZipCodeValidator applied to non-string schema.");
            // Fallback: ensure string then apply regex
            return z
                .string()
                .regex(/^\d{5}$/, { message })
                .refine(s => baseSchema.safeParse(s).success);
        }
    }

    // Add the async API validation as a second step
    // eslint-disable-next-line no-unused-vars
    async validateExternal(value, options = {}) {
        // Skip validation if empty (required rule should handle this)
        if (!value) return true;

        // Basic format validation (5 digits) is handled by Zod schema, but double check here
        if (!/^\d{5}$/.test(value)) {
            return false; // Should have been caught by Zod, but good failsafe
        }

        // Return from cache if available
        if (this._cache.has(value)) {
            return this._cache.get(value);
        }

        // Check if a request for this value is already pending
        if (this._pendingRequests.has(value)) {
            return await this._pendingRequests.get(value); // Wait for the existing request
        }

        // Perform the API call
        const validationPromise = (async () => {
            try {
                // Use the helper function to get the correct prefix
                const restPrefix = getRestUrlPrefix();
                const apiUrl = `${restPrefix}/usps/validateZip?zipCode=${value}`;
                const response = await fetch(apiUrl);

                if (!response.ok) {
                    // Handle HTTP errors (e.g., 404, 500)
                    // eslint-disable-next-line no-console
                    console.error(`ZIP validation API error for ${value}: ${response.status} ${response.statusText}`);
                    this._cache.set(value, false); // Assume invalid on API error
                    return false;
                }

                const data = await response.json();
                // Adjust based on the actual API response structure
                const isValid = data.success === true;

                // Cache the result
                this._cache.set(value, isValid);
                return isValid;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`ZIP code validation fetch error for ${value}:`, error);
                this._cache.set(value, false); // Assume invalid on network/parsing error
                return false;
            } finally {
                // Remove from pending requests once done
                this._pendingRequests.delete(value);
            }
        })();

        // Store the promise in pending requests
        this._pendingRequests.set(value, validationPromise);

        return await validationPromise;
    }

    getErrorMessage(field, options = {}) {
        // Use the message from options first, then data-attribute, then default
        const customMessage = options.message || field?.getAttribute("data-error-zipcode");
        return customMessage || "Please enter a valid US ZIP code";
    }
}
